import React from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';

import ImportTrucks from '@flp/components/logistics/import/trucks';
import ImportTruck from '@flp/components/logistics/import/truck';
import ImportConsignments from '@flp/components/logistics/import/consignments/consignments';
import ImportConsignment from '@flp/components/logistics/import/consignments/consignment';
import ImportBills from '@flp/components/logistics/import/bills/bills';
import DeliveryTrucks from '@flp/components/logistics/delivery/trucks';
import DeliveryTruck from '@flp/components/logistics/delivery/truck';
import Remainings from '@flp/components/warehouse/remainings/remainings';
import Remaining from '@flp/components/warehouse/remainings/remaining';
import ExtraConsignments from '@flp/components/warehouse/extra-consignments/extra-consignments';
import ExtraConsignment from '@flp/components/warehouse/extra-consignments/extra-consignment';
import Cities from '@flp/components/cities/cities';
import City from '@flp/components/cities/city';
import Tags from '@flp/components/tags/tags';
import Tag from '@flp/components/tags/tag';
import Consignees from '@flp/components/consignees/consignees';
import Consignee from '@flp/components/consignees/consignee';
import TradingPlatforms from '@flp/components/trading-platforms/trading-platforms';
import TradingPlatform from '@flp/components/trading-platforms/trading-platform';
import Tariffs from '@flp/components/tariffs/tariffs';
import Tariff from '@flp/components/tariffs/tariff';
import BillHeaders from '@flp/components/bills/bill-headers';
import Users from '@flp/components/users/users';
import User from '@flp/components/users/user';
import Contacts from '@flp/components/contacts/contacts';
import Contact from '@flp/components/contacts/contact';
import Orders from '@flp/components/orders/orders';
import Order from '@flp/components/orders/order';
import Documents from '@flp/components/documents/documents';
import Accounts from '@flp/components/accounts/accounts';
import Account from '@flp/components/accounts/account';
import Counterparties from '@flp/components/counterparties/counterparties';
import UserAccount from '@flp/components/user-account/user-account';
import AuditLogs from '@flp/components/audit-logs/audit-logs';
import Balances from '@flp/components/balances/balances';
import BalanceDetails from '@flp/components/balances/balance-details';
import CargoCarriers from '@flp/components/cargo-carriers/cargo-carriers';
import CargoCarrier from '@flp/components/cargo-carriers/cargo-carrier';
import Items from '@flp/components/items/items';
import Item from '@flp/components/items/item';
import Countries from '@flp/components/countries/countries';
import Country from '@flp/components/countries/country';
import PackageTemplates from '@flp/components/package-templates/package-templates';
import PackageTemplate from '@flp/components/package-templates/package-template';
import Acceptance from '@flp/components/warehouse/acceptance/acceptance';
import WarehouseConsignment from '@flp/components/warehouse/acceptance/warehouse-consignment';
import WarehouseTruck from '@flp/components/warehouse/acceptance/warehouse-truck';
import ShipmentJournals from '@flp/components/warehouse/shipment-journals/shipment-journals';
import ShipmentJournal from '@flp/components/warehouse/shipment-journals/shipment-journal';

import Waypoints from '@components/waypoints/waypoints';
import Companies from '@components/companies/companies';
import Company from '@components/companies/company';
import Performers from '@components/performers/performers';
import NewNotification from '@components/notifications/new-notification';
import Emails from '@components/notifications/emails';
import PushMessages from '@src/core/components/notifications/push-mesages';
import TelegramMessages from '@components/notifications/telegram-messages';
import MaxMessages from '@components/notifications/max-messages';
import SmsMessages from '@src/core/components/notifications/sms-messages';
import ManageRoutes from '@components/route-sheet/manage-routes';
import RouteSheet from '@components/route-sheet/route-sheet';

import UsersReport from '@src/core/components/reports/users-report';
import ImportTrucksReport from '@src/core/components/reports/import-trucks-report';

import { ProtectedRoute } from '@controls/protected-route';
import RedirectRoute from '@controls/redirect-route';

import MainLayout from '@flp/main-layout';

import { IUserSession } from '@entities/user-session';

import { Permission, hasPermission } from '@enums/permission';
import { UserBalanceType } from '@enums/user-balance-type';

import { serverFetch } from '@flp/server';

interface IAppRoutes {
    userSession: IUserSession;
    appVersion: string | undefined;
}

const AppRoutes = (props: IAppRoutes) => {
    const { userSession, appVersion } = props;

    return (
        <Routes>
            <Route path='/' element={<MainLayout appVersion={appVersion} />}>
                <Route index element={<Navigate replace to={`/flp/logistics/import`} />} />
                <Route path='flp' element={<RedirectRoute replace to={`/flp/logistics/import`} />} />

                <Route path='notifications/sms' element={<RedirectRoute replace to={`/flp/notifications/sms`} />} />
                <Route path='notifications/emails' element={<RedirectRoute replace to={`/flp/notifications/emails`} />} />
                <Route path='notifications/push' element={<RedirectRoute replace to={`/flp/notifications/push`} />} />

                <Route path='flp/logistics/import' element={<ImportTrucks />} />
                <Route path='flp/logistics/import/new' element={<ImportTruck />} />
                <Route path='flp/logistics/import/:id' element={<ImportTruck />} />
                <Route path='flp/logistics/import/consignments/:truckId' element={<ImportConsignments />} />
                <Route path='flp/logistics/import/consignments/new/:truckId' element={<ImportConsignment />} />

                <Route
                    path='flp/logistics/import/consignments/:truckId/:consignmentId'
                    element={
                        <ProtectedRoute
                            isAllowed={
                                hasPermission(userSession.permissions, Permission.ManageTruckConsignment) ||
                                hasPermission(userSession.permissions, Permission.ViewTruckConsignment)
                            }
                            redirectPath={'/'}
                            children={<ImportConsignment />}
                        />
                    }
                />

                <Route
                    path='flp/logistics/import/tariffs/:truckId'
                    element={
                        <ProtectedRoute
                            isAllowed={
                                hasPermission(userSession.permissions, Permission.ManageTransportBill) ||
                                hasPermission(userSession.permissions, Permission.EditTransportBill) ||
                                hasPermission(userSession.permissions, Permission.ViewTransportBill)
                            }
                            redirectPath={'/'}
                            children={<ImportBills />}
                        />
                    }
                />

                <Route path='flp/logistics/delivery' element={<DeliveryTrucks />} />
                <Route path='flp/logistics/delivery/new/:journalId' element={<DeliveryTruck />} />
                <Route path='flp/logistics/delivery/:truckId/:journalId?' element={<DeliveryTruck />} />

                <Route path='flp/warehouse/acceptance' element={<Acceptance serverFetch={serverFetch} />} />
                <Route path='flp/warehouse/acceptance/:id' element={<WarehouseTruck serverFetch={serverFetch} />} />
                <Route
                    path='flp/warehouse/acceptance/consignments/:truckId/:consignmentId'
                    element={<WarehouseConsignment serverFetch={serverFetch} />}
                />
                <Route path='flp/warehouse/remainings' element={<Remainings />} />
                <Route path='flp/warehouse/remainings/:boxGroupId' element={<Remaining />} />
                <Route path='flp/warehouse/remainings/shipment/:journalId?' element={<Remainings />} />
                <Route path='flp/warehouse/extraconsignments' element={<ExtraConsignments />} />
                <Route path='flp/warehouse/extraconsignments/new' element={<ExtraConsignment />} />
                <Route
                    path='flp/warehouse/extraconsignments/consignments/:consignmentId'
                    element={<WarehouseConsignment serverFetch={serverFetch} />}
                />
                <Route path='flp/warehouse/shipmentjournals' element={<ShipmentJournals serverFetch={serverFetch} />} />
                <Route path='flp/warehouse/shipmentjournals/:id' element={<ShipmentJournal serverFetch={serverFetch} />} />
                <Route path='flp/warehouse/shipmentjournals/:journalId/truck/new' element={<DeliveryTruck />} />
                <Route path='flp/warehouse/shipmentjournals/:journalId/truck/:truckId' element={<DeliveryTruck />} />
                <Route
                    path='flp/warehouse/shipmentjournals/manageoutes/:journalId/:journalNumber'
                    element={<ManageRoutes serverFetch={serverFetch} />}
                />

                <Route path='flp/warehouse/performers/:areaType/:truckId?' element={<Performers serverFetch={serverFetch} />} />
                <Route path='flp/warehouse/routesheet/:journalId' element={<RouteSheet serverFetch={serverFetch} />} />

                <Route
                    element={
                        <ProtectedRoute isAllowed={hasPermission(userSession.permissions, Permission.ManageTariff)} redirectPath={'/'} />
                    }
                >
                    <Route path='flp/finance/tariffs' element={<Tariffs />} />
                    <Route path='flp/finance/tariffs/new/:headerId' element={<Tariff />} />
                    <Route path='flp/finance/tariffs/:id/:isCopy?' element={<Tariff />} />
                </Route>

                <Route
                    path='flp/finance/bills/:userId?'
                    element={
                        <ProtectedRoute
                            isAllowed={
                                hasPermission(userSession.permissions, Permission.ManageTransportBill) ||
                                hasPermission(userSession.permissions, Permission.EditTransportBill) ||
                                hasPermission(userSession.permissions, Permission.ViewTransportBill) ||
                                hasPermission(userSession.permissions, Permission.SetPaid)
                            }
                            redirectPath={'/'}
                            children={<BillHeaders />}
                        />
                    }
                />

                <Route
                    element={
                        <ProtectedRoute
                            isAllowed={
                                hasPermission(userSession.permissions, Permission.ManageBalance) ||
                                hasPermission(userSession.permissions, Permission.ViewBalance)
                            }
                            redirectPath={'/'}
                        />
                    }
                >
                    <Route
                        path='flp/finance/balances'
                        element={<Balances serverFetch={serverFetch} userBalanceType={UserBalanceType.Regular} />}
                    />
                    <Route
                        path='flp/finance/balances/:userId'
                        element={<BalanceDetails serverFetch={serverFetch} userBalanceType={UserBalanceType.Regular} />}
                    />
                    <Route
                        path='flp/finance/whitebalances'
                        element={<Balances serverFetch={serverFetch} userBalanceType={UserBalanceType.White} />}
                    />
                    <Route
                        path='flp/finance/whitebalances/:userId'
                        element={<BalanceDetails serverFetch={serverFetch} userBalanceType={UserBalanceType.White} />}
                    />
                </Route>

                <Route path='flp/settings/countries' element={<Countries serverFetch={serverFetch} />} />
                <Route path='flp/settings/countries/new' element={<Country serverFetch={serverFetch} />} />
                <Route path='flp/settings/countries/:id' element={<Country serverFetch={serverFetch} />} />

                <Route path='flp/settings/cities' element={<Cities />} />
                <Route path='flp/settings/cities/new' element={<City />} />
                <Route path='flp/settings/cities/:id' element={<City />} />

                <Route path='flp/settings/consignees' element={<Consignees />} />
                <Route path='flp/settings/consignees/new' element={<Consignee />} />
                <Route path='flp/settings/consignees/:id' element={<Consignee />} />

                <Route path='flp/settings/waypoints' element={<Waypoints serverFetch={serverFetch} />} />

                <Route path='flp/settings/tags' element={<Tags />} />
                <Route path='flp/settings/tags/new' element={<Tag />} />
                <Route path='flp/settings/tags/:id' element={<Tag />} />

                <Route path='flp/settings/tradingplatforms' element={<TradingPlatforms />} />
                <Route path='flp/settings/tradingplatforms/new' element={<TradingPlatform />} />
                <Route path='flp/settings/tradingplatforms/:id' element={<TradingPlatform />} />

                <Route path='flp/settings/users' element={<Users />} />
                <Route path='flp/settings/users/new' element={<User />} />
                <Route path='flp/settings/users/:id' element={<User />} />

                <Route path='flp/settings/counterparties' element={<Counterparties />} />

                <Route path='flp/orders/:userId' element={<Orders />} />
                <Route path='flp/orders/:userId/:orderId' element={<Order />} />

                <Route
                    path='flp/notifications/newnotification'
                    element={<NewNotification serverFetch={serverFetch} modulePath='flp' serviceName='Посадка' />}
                />
                <Route path='flp/notifications/sms' element={<SmsMessages serverFetch={serverFetch} />} />
                <Route path='flp/notifications/emails' element={<Emails serverFetch={serverFetch} />} />
                <Route path='flp/notifications/push' element={<PushMessages serverFetch={serverFetch} />} />
                <Route path='flp/notifications/telegram' element={<TelegramMessages serverFetch={serverFetch} />} />
                <Route path='flp/notifications/max' element={<MaxMessages serverFetch={serverFetch} />} />

                <Route path='flp/documents' element={<Documents />} />

                <Route
                    element={
                        <ProtectedRoute
                            isAllowed={hasPermission(userSession.permissions, Permission.ViewUsersReport)}
                            redirectPath={'/'}
                        />
                    }
                >
                    <Route path='flp/reports/users' element={<UsersReport modulePath='flp' serverFetch={serverFetch} />} />
                </Route>

                <Route
                    element={
                        <ProtectedRoute
                            isAllowed={hasPermission(userSession.permissions, Permission.ViewImportTrucksReport)}
                            redirectPath={'/'}
                        />
                    }
                >
                    <Route
                        path='flp/reports/importtrucks'
                        element={<ImportTrucksReport useDiscounts={false} serverFetch={serverFetch} />}
                    />
                </Route>

                <Route path='flp/settings/contacts' element={<Contacts />} />
                <Route path='flp/settings/contacts/new' element={<Contact />} />
                <Route path='flp/settings/contacts/:id' element={<Contact />} />

                <Route path='flp/settings/accounts' element={<Accounts />} />
                <Route path='flp/settings/accounts/new' element={<Account />} />
                <Route path='flp/settings/accounts/:id' element={<Account />} />

                <Route path='flp/settings/items' element={<Items serverFetch={serverFetch} />} />
                <Route path='flp/settings/items/new' element={<Item serverFetch={serverFetch} />} />
                <Route path='flp/settings/items/:id' element={<Item serverFetch={serverFetch} />} />

                <Route path='flp/settings/packagetemplates' element={<PackageTemplates />} />
                <Route path='flp/settings/packagetemplates/new' element={<PackageTemplate />} />
                <Route path='flp/settings/packagetemplates/:id' element={<PackageTemplate />} />

                <Route path='flp/settings/companies' element={<Companies serverFetch={serverFetch} />} />
                <Route path='flp/settings/companies/new' element={<Company serverFetch={serverFetch} />} />
                <Route path='flp/settings/companies/:id' element={<Company serverFetch={serverFetch} />} />

                <Route path='flp/settings/cargocarriers' element={<CargoCarriers />} />
                <Route path='flp/settings/cargocarriers/new' element={<CargoCarrier />} />
                <Route path='flp/settings/cargocarriers/:id' element={<CargoCarrier />} />

                <Route path='flp/useraccount' element={<UserAccount />} />
                <Route path='flp/auditlogs' element={<AuditLogs />} />
            </Route>
        </Routes>
    );
};

export default AppRoutes;
